import {
  Box,
  Breadcrumbs,
  Link as MUILink,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useBreadcrumbs } from "../hooks/useBreadcrumbs";
import { usePreviewFeature } from "../providers/previewFeature";
import { PREVIEW_ICON_FONT_SIZE, PREVIEW_ICON_GAP } from "./PreviewFeature/constants";
import { PreviewFeatureIndicator } from "./PreviewFeature/PreviewFeatureIndicator";

export default function NavBreadcrumbs() {
  const { breadcrumbs } = useBreadcrumbs();
  const { isPreview, activeVariant } = usePreviewFeature();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [fontSize, setFontSize] = useState("1.25rem");
  const breadcrumbsRef = useRef(null);

  useEffect(() => {
    const adjustFontSize = () => {
      if (breadcrumbsRef.current) {
        const maxHeight = 60;

        if ((breadcrumbsRef.current as HTMLDivElement).offsetHeight > maxHeight) {
          setFontSize("1.25rem");
        } else {
          setFontSize(isLargeScreen ? "1.5rem" : "1.25rem");
        }
      }
    };
    adjustFontSize();
  }, [breadcrumbs, isLargeScreen]);

  const displayBreadcrumbs = useMemo(() => {
    return isLargeScreen ? breadcrumbs : breadcrumbs.slice(-1);
  }, [isLargeScreen, breadcrumbs]);

  return (
    <Breadcrumbs
      ref={breadcrumbsRef}
      aria-label="breadcrumb"
      separator={"/"}
      sx={{
        maxHeight: "100%",
        overflow: "hidden",
        maxWidth: "100%",
      }}
    >
      {displayBreadcrumbs.map((breadcrumb, idx) => {
        const isTerminalCrumb = idx === displayBreadcrumbs.length - 1;
        const showPreviewSuffix = isPreview && isTerminalCrumb;

        if (!breadcrumb.label) {
          return <Skeleton key={`skeleton-${idx}`} width={200} />;
        }

        if (breadcrumb.to && !isTerminalCrumb) {
          return (
            <MUILink
              key={breadcrumb.label}
              underline="hover"
              color="primary"
              sx={{
                fontSize,
                textDecoration: "none",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                display: "block",
              }}
              to={`${breadcrumb.to}`}
              component={Link}
            >
              {breadcrumb.label}
            </MUILink>
          );
        }

        return (
          <Typography
            component="div"
            sx={{
              fontSize,
              textDecoration: "none",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              display: "flex",
              alignItems: "center",
              flexWrap: "nowrap",
              gap: showPreviewSuffix ? PREVIEW_ICON_GAP : 0,
              minWidth: 0,
            }}
            key={breadcrumb.label}
          >
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {breadcrumb.label}
              {breadcrumb.addendum && (
                <span style={{ color: theme.palette.grey[500] }}>
                  {" "}
                  {`- ${breadcrumb.addendum}`}
                </span>
              )}
            </Box>
            {showPreviewSuffix && (
              <PreviewFeatureIndicator
                fontSize={PREVIEW_ICON_FONT_SIZE}
                inline
                color={theme.palette.secondary.main}
                variant={activeVariant}
              />
            )}
          </Typography>
        );
      })}
    </Breadcrumbs>
  );
}
